// socketRequest.ts - Promise-based WebSocket request utility

import { getSocket } from "./socketMiddleware";
import { isEnvBrowser } from "./misc";

interface PendingRequest {
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
  timeout: NodeJS.Timeout;
}

// Store pending requests by their unique ID
const pendingRequests: Map<string, PendingRequest> = new Map();

// Message handlers for unsolicited server messages
type MessageHandler = (data: any) => void;
const messageHandlers: Map<string, MessageHandler[]> = new Map();

let requestIdCounter = 0;

/**
 * Generate a unique request ID
 */
function generateRequestId(): string {
  return `req_${Date.now()}_${++requestIdCounter}`;
}

/**
 * Send a request via WebSocket and wait for a response
 * Works exactly like the old fetchNui - same signature and behavior
 *
 * @param eventName - The event/message type (e.g., "GetHouseData", "SaveSettings")
 * @param data - The payload data to send (optional)
 * @param mockData - Mock data to return in browser mode for testing (optional)
 * @returns Promise that resolves with the server response
 *
 * @example
 * const hardmoneyData = await socketRequest("GetHouseData", { houseId: 123 });
 *
 * @example
 * // With mock data for browser testing
 * const mockHouse = { houseId: 123, owner: "John" };
 * const hardmoneyData = await socketRequest("GetHouseData", { houseId: 123 }, mockHouse);
 */
export async function socketRequest<T = unknown>(
  eventName: string,
  data?: unknown,
  mockData?: T
): Promise<T> {
  // Browser mode: Return mock data for development/testing
  if (isEnvBrowser() && mockData) {
    console.log(`🧪 [Browser Mode] Returning mock data for: ${eventName}`);
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(mockData);
      }, 1000); // Simulate network delay
    });
  }

  return new Promise((resolve, reject) => {
    const socket = getSocket();

    if (!socket || socket.readyState !== WebSocket.OPEN) {
      reject(new Error(`WebSocket is not connected. Cannot send request: ${eventName}`));
      return;
    }

    const requestId = generateRequestId();

    // Set up timeout (10 seconds default, matching old fetchNui behavior)
    const timeout = setTimeout(() => {
      pendingRequests.delete(requestId);
      reject(new Error(`Request timeout: ${eventName} (10000ms)`));
    }, 10000);

    // Store the pending request
    pendingRequests.set(requestId, { resolve, reject, timeout });

    // Send the request with requestId
    // If data is an object, spread it; otherwise wrap it in a data property
    const messagePayload = data && typeof data === 'object' && !Array.isArray(data)
      ? { requestId, type: eventName, ...data as Record<string, unknown> }
      : { requestId, type: eventName, data };

    const message = JSON.stringify(messagePayload);

    try {
      socket.send(message);
      console.log(`📤 WebSocket request sent: ${eventName} (ID: ${requestId})`);
    } catch (error) {
      clearTimeout(timeout);
      pendingRequests.delete(requestId);
      reject(error);
    }
  });
}

/**
 * Handle incoming WebSocket response (called from socketMiddleware)
 * Resolves the promise for request-response pattern
 *
 * @param message - The parsed WebSocket message
 * @returns true if handled as a response, false if it's an unsolicited message
 */
export function handleSocketResponse(message: any): boolean {
  // Check if this is a response to a pending request
  if (message.requestId && pendingRequests.has(message.requestId)) {
    const { resolve, reject, timeout } = pendingRequests.get(message.requestId)!;

    clearTimeout(timeout);
    pendingRequests.delete(message.requestId);

    if (message.error) {
      console.error(`❌ WebSocket request failed: ${message.error}`);
      reject(new Error(message.error));
    } else {
      console.log(`✅ WebSocket response received (ID: ${message.requestId})`);
      resolve(message.data || message);
    }

    return true; // Handled as a response
  }

  return false; // Not a response, it's an unsolicited message
}

/**
 * Register a handler for unsolicited server messages
 * Use this for server-initiated broadcasts that aren't request-response
 *
 * @param messageType - The type of message to listen for (e.g., "notification", "alert")
 * @param handler - Callback function to handle the message
 * @returns Unsubscribe function
 *
 * @example
 * const unsubscribe = onSocketMessage("notification", (data) => {
 *   console.log("New notification:", data);
 * });
 *
 * // Later, to unsubscribe:
 * unsubscribe();
 */
export function onSocketMessage(messageType: string, handler: MessageHandler): () => void {
  if (!messageHandlers.has(messageType)) {
    messageHandlers.set(messageType, []);
  }

  messageHandlers.get(messageType)!.push(handler);
  //console.log(`🔔 Registered handler for message type: ${messageType}`);

  // Return unsubscribe function
  return () => {
    const handlers = messageHandlers.get(messageType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  };
}

/**
 * Dispatch unsolicited messages to registered handlers
 * Called from socketMiddleware for messages that aren't responses
 *
 * @param messageType - The type of message
 * @param data - The message data
 */
export function dispatchSocketMessage(messageType: string, data: any): void {
  const handlers = messageHandlers.get(messageType);

  if (handlers && handlers.length > 0) {
    console.log(`📨 Dispatching message type: ${messageType} to ${handlers.length} handler(s)`);
    handlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in message handler for ${messageType}:`, error);
      }
    });
  }
}

/**
 * Clean up all pending requests (e.g., on disconnect)
 */
export function clearPendingRequests(): void {
  pendingRequests.forEach(({ reject, timeout }) => {
    clearTimeout(timeout);
    reject(new Error("WebSocket disconnected"));
  });
  pendingRequests.clear();
  console.log("🧹 Cleared all pending requests");
}
